import { ACTIVE_OPS_LOGISTICS_BY_ID } from '../catalog/activeLogisticsCatalog';
import type { OpsFlow, OpsGate } from '../types';
import {
  completionForLogisticId,
  flowReferencedLogisticIds,
  gateStatusForFlow,
  type GateStatus,
  type SsotSources,
} from './completion';

export type ReadinessBadgeTone = 'danger' | 'warn' | 'muted';

export type ReadinessBadge = {
  id: string;
  label: string;
  tone: ReadinessBadgeTone;
};

export type OpsCardReadiness = {
  gateStatus: Record<OpsGate, GateStatus>;
  missingCount: number;
  topMissingLabels: string[];
  needsDueDate: boolean;
  badges: ReadinessBadge[];
};

/**
 * Gate-derived badges, ordered by operational signal strength.
 * Suggested-move badges are intentionally NOT computed here (deferred).
 */
const GATE_BADGES: Array<{ gate: OpsGate; id: string; label: string; tone: ReadinessBadgeTone }> = [
  { gate: 'invoicing', id: 'billing', label: 'Missing billing', tone: 'warn' },
  { gate: 'teacher_assignment', id: 'teacher', label: 'No teacher', tone: 'warn' },
  { gate: 'marketing', id: 'marketing', label: 'Marketing incomplete', tone: 'muted' },
  { gate: 'scheduling', id: 'scheduling', label: 'Scheduling incomplete', tone: 'muted' },
  { gate: 'parent_comms', id: 'parent_comms', label: 'Parent comms incomplete', tone: 'muted' },
];

export function buildCardReadiness(
  flow: OpsFlow,
  sources: SsotSources,
  opts: { needsDueDate: boolean },
): OpsCardReadiness {
  const gateStatus = gateStatusForFlow(flow, sources);

  // Collect incomplete referenced logistics for count + highest-signal labels.
  const incompleteDefs: Array<{ label: string; systemRequired: boolean }> = [];
  for (const logisticId of flowReferencedLogisticIds(flow)) {
    const def: any = (ACTIVE_OPS_LOGISTICS_BY_ID as any)[logisticId] || null;
    if (!def) continue;
    const complete = completionForLogisticId(flow, logisticId, sources) === true;
    if (!complete) {
      incompleteDefs.push({
        label: String(def.label || logisticId),
        systemRequired: Boolean(def.systemRequired),
      });
    }
  }

  // Highest-signal first: system-required logistics, then the rest, alphabetized.
  incompleteDefs.sort((a, b) => {
    if (a.systemRequired !== b.systemRequired) return a.systemRequired ? -1 : 1;
    return a.label.localeCompare(b.label);
  });
  const topMissingLabels = incompleteDefs.slice(0, 3).map((d) => d.label);

  const badges: ReadinessBadge[] = [];
  if (opts.needsDueDate) badges.push({ id: 'no_due', label: 'No due date', tone: 'danger' });
  for (const b of GATE_BADGES) {
    if (gateStatus[b.gate] === 'incomplete') {
      badges.push({ id: b.id, label: b.label, tone: b.tone });
    }
  }

  return {
    gateStatus,
    missingCount: incompleteDefs.length,
    topMissingLabels,
    needsDueDate: Boolean(opts.needsDueDate),
    badges,
  };
}
